import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  usePutAppFeature,
  useGetAppFeatures,
} from '../../../services/redux/api/appConfigApi';
import { IAppFeature } from '../../../types/interfaces/app-feature';
import { FormikProps, useFormik } from 'formik';

export function useAppFeaturesComponent(classes) {
  const { t } = useTranslation();
  const { data: appFeatures } = useGetAppFeatures();
  const [putAppFeature] = usePutAppFeature();
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      isActive: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      appFeatures?.filter((feature: IAppFeature) => {
        const isActiveFilterId = filterFormik.values.isActive?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const includesFilter =
          String(feature.key ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(feature.title ?? '')
            .toLowerCase()
            .includes(filterValue);

        if (!includesFilter) return false;

        if (isActiveFilterId !== 'all') {
          const isActive = !!feature.isActive;
          if (
            (isActiveFilterId === true && !isActive) ||
            (isActiveFilterId === false && isActive)
          ) {
            return false;
          }
        }

        return true;
      }),
    [appFeatures, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    access?.appFeatures?.canEdit ?? false,
    (feature: IAppFeature, value) => {
      putAppFeature({ id: feature.id, body: { isActive: value } });
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.APP_FEATURE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  const filterIsActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    filter,
    setFilter,
    filterFormik,
    filterIsActiveOptions,
  };
}
